import React, { useEffect, useState } from 'react';
import { formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale';

/**
 * One-line banner shown only while the browser reports offline.
 * Carries the timestamp of the last server data we managed to display
 * (passed in via lastDataAt — usually the network-status timestamp the
 * app already tracks) so the user knows how stale the cached state is.
 */
const OfflineBanner = ({ lastDataAt }) => {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online) return null;

  let stale = '';
  if (lastDataAt) {
    try {
      const d = parseISO(lastDataAt);
      if (isValid(d)) stale = formatDistanceToNow(d, { addSuffix: true, locale: enUS });
    } catch { /* ignore */ }
  }

  return (
    <div className="offline-banner" role="status" aria-live="polite">
      You're offline — showing data from {stale || 'an earlier session'}.
    </div>
  );
};

export default OfflineBanner;
